/** Shared display helpers for email-template names in admin UI. */

export const TEMPLATE_TYPE_LABEL = {
  invite: "Invite",
  resend: "Resend",
  auto_reminder: "Reminder",
};

export const TYPE_BADGE_CLASS = {
  invite: "bg-sky-50 text-sky-800",
  resend: "bg-amber-50 text-amber-800",
  auto_reminder: "bg-[#004f73]/10 text-[#004f73]",
};

const AUDIENCE_SHORT = {
  "CEO & Leadership Team": "CEO & Exec",
  "PE & Board": "PE & Board",
};

const TITLE_MAX = 30;
const PLACEHOLDER_LABEL = /^(new (reminder )?template|untitled reminder( \d+)?)$/i;

/** Every option in a reminder row is already an automated-reminder template
 *  for that row's audience, so a seeded label like "CEO & Leadership Team —
 *  Automated Reminder (Stage 1, ~26h)" spends its width restating the row
 *  before it says the one thing that tells the options apart. Trim the known
 *  prefix down to that distinguishing tail, and leave admin-authored labels
 *  untouched. */
export function templateOptionLabel(label, audienceLabel) {
  const full = String(label || "").trim();
  let text = full;
  if (audienceLabel && text.toLowerCase().startsWith(audienceLabel.toLowerCase())) {
    text = text.slice(audienceLabel.length);
  }
  text = text
    .replace(/^\s*[—–-]\s*/, "")
    .replace(/^automated reminder\s*/i, "")
    .replace(/^\((.*)\)$/, "$1")
    .replace(/~/g, "")
    .trim();
  return text || full;
}

export function shortAudienceLabel(audienceLabel) {
  const full = String(audienceLabel || "").trim();
  if (!full) return "";
  if (AUDIENCE_SHORT[full]) return AUDIENCE_SHORT[full];
  if (full.length <= 16) return full;
  return full.replace(/Leadership Team/i, "Exec").replace(/\s+Team$/i, "");
}

export function templateTiming(label) {
  const match = String(label || "").match(/~?\s*(\d+)\s*h/i);
  return match ? `${match[1]}h` : "";
}

export function templateStage(template) {
  if (template?.stage != null && template.stage !== "") return Number(template.stage);
  const match = String(template?.label || "").match(/stage\s*(\d+)/i);
  return match ? Number(match[1]) : null;
}

function clipTitle(text) {
  const value = String(text || "").trim();
  if (value.length <= TITLE_MAX) return value;
  return `${value.slice(0, TITLE_MAX - 1).trimEnd()}…`;
}

/** Card / preview title: function only, never audience or delay. */
export function templateCardTitle(template, audienceLabel) {
  const type = template?.template_type;
  const label = String(template?.label || "").trim();

  if (type === "invite") return "Invitation";
  if (type === "resend") return "Follow-up Resend";
  if (/^new (reminder )?template$/i.test(label)) return "Untitled reminder";
  if (/^untitled reminder/i.test(label)) return clipTitle(label);

  const stage = templateStage(template);
  if (type === "auto_reminder" && stage) return `Stage ${stage} Reminder`;

  const stripped = templateOptionLabel(label, audienceLabel);
  const staged = String(stripped).match(/^stage\s*(\d+)/i);
  if (staged) return `Stage ${staged[1]} Reminder`;
  return clipTitle(stripped);
}

export function templateSelectLabel(template, audienceLabel) {
  const { title, timing } = templateDisplay(template, audienceLabel);
  return timing ? `${title} · ${timing}` : title;
}

export function templateDisplay(template, audienceLabel) {
  const type = template?.template_type || "";
  return {
    title: templateCardTitle(template, audienceLabel),
    typeLabel: TEMPLATE_TYPE_LABEL[type] || type,
    typeClass: TYPE_BADGE_CLASS[type] || "bg-slate-100 text-slate-600",
    audienceShort: shortAudienceLabel(audienceLabel),
    audienceFull: String(audienceLabel || "").trim(),
    timing: templateTiming(template?.label),
    stage: templateStage(template),
  };
}

export function nextUntitledReminderLabel(templates) {
  const count = (templates || []).filter((t) => PLACEHOLDER_LABEL.test(String(t.label || ""))).length;
  return count === 0 ? "Untitled reminder" : `Untitled reminder ${count + 1}`;
}
